import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminDashboard from '../../../layouts/AdminDashboard/AdminDashboard';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme, alpha } from '@mui/material/styles';
import { AuditLogEntry, getAuditLog } from './AuditLogService';

const PAGE_SIZE = 25;

const TARGET_TYPES = [
  '',
  'User',
  'Subscription',
  'Group',
  'Membership',
  'Invitation',
  'BusinessTemplate',
];

const actionColour = (
  action: string,
): 'default' | 'success' | 'warning' | 'error' | 'info' => {
  const a = action.toLowerCase();
  if (a.includes('enabled') || a.includes('granted') || a.includes('created'))
    return 'success';
  if (a.includes('disabled') || a.includes('deleted') || a.includes('removed'))
    return 'error';
  if (a.includes('updated') || a.includes('extended') || a.includes('changed'))
    return 'warning';
  if (a.includes('trial') || a.includes('expired') || a.includes('ended'))
    return 'info';
  return 'default';
};

const fmtDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
};

const prettyJson = (raw: string | null): string => {
  if (!raw) return '—';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

// ─── Detail dialog ────────────────────────────────────────────────────────────

interface DetailDialogProps {
  entry: AuditLogEntry | null;
  onClose: () => void;
}

const DetailDialog = ({ entry, onClose }: DetailDialogProps): JSX.Element => (
  <Dialog open={!!entry} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box>
        <Typography variant="h6" fontWeight={700}>
          Audit Detail
        </Typography>
        {entry && (
          <Typography variant="body2" color="text.secondary">
            {entry.action} · {fmtDate(entry.timestamp)}
          </Typography>
        )}
      </Box>
      <IconButton onClick={onClose} size="small">
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers>
      {entry && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {[
              { label: 'Actor', value: entry.actor },
              { label: 'Action', value: entry.action },
              { label: 'Target Type', value: entry.targetType ?? '—' },
              { label: 'Target ID', value: entry.targetId ?? '—' },
              { label: 'Reason', value: entry.reason ?? '—' },
            ].map(({ label, value }) => (
              <Box key={label}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 700, textTransform: 'uppercase' }}
                >
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>

          {[
            { label: 'Before', value: entry.beforeValue },
            { label: 'After', value: entry.afterValue },
          ].map(({ label, value }) => (
            <Box key={label}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700, textTransform: 'uppercase' }}
              >
                {label}
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  mt: 0.5,
                  p: 1.5,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: 200,
                }}
              >
                <Typography
                  component="pre"
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    m: 0,
                  }}
                >
                  {prettyJson(value)}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      )}
    </DialogContent>
  </Dialog>
);

// ─── Main page ────────────────────────────────────────────────────────────────

const AuditLogPage = (): JSX.Element => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();

  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [totalElements, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [actorFilter, setActorFilter] = useState('');
  const [targetTypeFilter, setTargetTypeFilter] = useState(() => searchParams.get('targetType') ?? '');
  const [actionFilter, setActionFilter] = useState(() => searchParams.get('action') ?? '');
  const [pendingActor, setPendingActor] = useState('');

  const [selected, setSelected] = useState<AuditLogEntry | null>(null);

  const load = useCallback(
    (p: number) => {
      setLoading(true);
      setError('');
      getAuditLog(
        p - 1,
        PAGE_SIZE,
        actorFilter || undefined,
        targetTypeFilter || undefined,
        actionFilter || undefined,
      )
        .then((res) => {
          setEntries(res.data.content);
          setTotal(res.data.totalElements);
          setTotalPages(res.data.totalPages);
        })
        .catch(() => setError('Could not load audit log.'))
        .finally(() => setLoading(false));
    },
    [actorFilter, targetTypeFilter, actionFilter],
  );

  useEffect(() => {
    load(page);
  }, [load, page]);

  const handleSearch = () => {
    setActorFilter(pendingActor);
    setPage(1);
  };

  const handleTargetType = (v: string) => {
    setTargetTypeFilter(v);
    setPage(1);
  };
  const handleAction = (v: string) => {
    setActionFilter(v);
    setPage(1);
  };

  const handleClear = () => {
    setActorFilter('');
    setPendingActor('');
    setTargetTypeFilter('');
    setActionFilter('');
    setPage(1);
  };

  const hasFilters = !!(actorFilter || targetTypeFilter || actionFilter);

  return (
    <AdminDashboard>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${theme.palette.background.default} 100%)`,
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Container maxWidth="xl">
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" fontWeight={700}>
              Audit Log
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Append-only record of sensitive admin actions.
              {!loading && ` ${totalElements.toLocaleString()} entries total.`}
            </Typography>
          </Box>

          {/* Filters */}
          <Card sx={{ borderRadius: 2, boxShadow: theme.shadows[1], mb: 3 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                <TextField
                  placeholder="Filter by actor email…"
                  size="small"
                  value={pendingActor}
                  onChange={(e) => setPendingActor(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 260 }}
                />

                <Select
                  size="small"
                  displayEmpty
                  value={targetTypeFilter}
                  onChange={(e) => handleTargetType(e.target.value)}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="">All target types</MenuItem>
                  {TARGET_TYPES.filter(Boolean).map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>

                <TextField
                  placeholder="Filter by action…"
                  size="small"
                  value={actionFilter}
                  onChange={(e) => handleAction(e.target.value)}
                  sx={{ minWidth: 200 }}
                />

                {hasFilters && (
                  <Chip
                    label="Clear filters"
                    onDelete={handleClear}
                    size="small"
                    variant="outlined"
                    sx={{ alignSelf: 'center' }}
                  />
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Error */}
          {error && (
            <Card
              sx={{
                borderRadius: 2,
                mb: 3,
                bgcolor: alpha(theme.palette.error.main, 0.06),
              }}
            >
              <CardContent>
                <Typography color="error">{error}</Typography>
              </CardContent>
            </Card>
          )}

          {/* Table */}
          <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[2] }}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}
                    >
                      {[
                        'Timestamp',
                        'Actor',
                        'Action',
                        'Target',
                        'Reason',
                        '',
                      ].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700, py: 1.5 }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                          <CircularProgress size={28} />
                        </TableCell>
                      </TableRow>
                    ) : entries.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          align="center"
                          sx={{ py: 6, color: 'text.secondary' }}
                        >
                          No audit entries found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      entries.map((e) => (
                        <TableRow
                          key={e.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => setSelected(e)}
                        >
                          <TableCell
                            sx={{
                              whiteSpace: 'nowrap',
                              color: 'text.secondary',
                              fontSize: '0.78rem',
                            }}
                          >
                            {fmtDate(e.timestamp)}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {e.actor}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={e.action}
                              color={actionColour(e.action)}
                              size="small"
                              sx={{ fontWeight: 600, fontSize: '0.72rem' }}
                            />
                          </TableCell>
                          <TableCell>
                            {e.targetType && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {e.targetType}
                                {e.targetId ? ` #${e.targetId}` : ''}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                              sx={{ maxWidth: 220 }}
                            >
                              {e.reason ?? '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View details">
                              <IconButton
                                size="small"
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  setSelected(e);
                                }}
                              >
                                <InfoOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 2,
                    py: 1.5,
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {(page - 1) * PAGE_SIZE + 1}–
                    {Math.min(page * PAGE_SIZE, totalElements)} of{' '}
                    {totalElements.toLocaleString()}
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, v) => setPage(v)}
                    size="small"
                    color="primary"
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Container>
      </Box>

      <DetailDialog entry={selected} onClose={() => setSelected(null)} />
    </AdminDashboard>
  );
};

export default AuditLogPage;
